import { mainLogger as log } from "@/app/logger"
import {
  CategoriesList,
  Category,
  Firm,
  FirmsList,
  Fund,
  FundsList,
  Interval,
  MetaTerms,
  Term,
} from "@/app/types"
import {
  isArrayWithItems,
  isNotArray,
  isObjectWithProps,
  termId,
} from "@/app/utils"

// the list of data that can be provided in the constructor of this Lookup
// all of them are optional - if not given, the mapping is initialized to an empty Map<>
type LookupSourceData = {
  categoriesList?: CategoriesList
  firmsList?: FirmsList
  fundsList?: FundsList
  metaTerms?: MetaTerms
}

// TODO - save a readonly copy (Object.freeze) of the lists and add getAllXXX() methods,
// TODO - the getXXX() method should return a readonly copy of the object

// the lookup class creates a reverse mapping of ID's -> Objects and exposes getXXX() functions
// the singleton is created in the AppDataProvider after all lists have been loaded
export class Lookup {
  // initialize with private empty Map<> so all id's will return null objects
  #categoriesMapping = new Map<string, Category>()
  #firmsMapping = new Map<string, Firm>()
  #firmsRefMapping = new Map<string, Firm>()
  #fundsMapping = new Map<string, Fund>()
  #fundsRefMapping = new Map<string, Fund>()
  #termsMapping = new Map<string, Term>()

  // the constructor populates the dictionaries by looping thru the passed data
  // optionally, it takes an existing Lookup to use as fallback source of data
  constructor(data?: LookupSourceData, from?: Lookup) {
    if (from) {
      this.#categoriesMapping = from.#categoriesMapping
      this.#firmsMapping = from.#firmsMapping
      this.#firmsRefMapping = from.#firmsRefMapping
      this.#fundsMapping = from.#fundsMapping
      this.#fundsRefMapping = from.#fundsRefMapping
      this.#termsMapping = from.#termsMapping
    }

    if (!data) {
      log.warn("Cannot initialize Lookup with null data")
      return
    }

    const termTypes = ["periodic", "ranking"]
    if (!isObjectWithProps(data.metaTerms, termTypes)) {
      log.error(
        "Missing ",
        termTypes,
        "in data.MetaTerms, Got:",
        data.metaTerms
      )
    } else {
      const newTermsMapping = new Map<string, Term>()
      // for both periodic and ranking terms
      termTypes.map((termType) => {
        const intervals = data.metaTerms?.[termType]
        // check if all intervals are present
        if (!isObjectWithProps(intervals, Interval.NAMES)) {
          log.error(
            "Missing ",
            Interval.NAMES,
            "in data.MetaTerms[",
            termType,
            "], Got:",
            intervals
          )
          return
        }
        Interval.NAMES.map((interval) => {
          const isPeriodic = termType == "periodic"
          const intervalTerms = intervals[interval]
          if (isNotArray(intervalTerms)) {
            log.error(
              "Unexpected object for interval:",
              interval,
              "in data.MetaTerms[",
              termType,
              "], Got:",
              intervalTerms
            )
            return
          }
          // save all the terms in the termsMapping, using the synthetic _termId()
          intervalTerms.forEach((term) => {
            newTermsMapping.set(
              termId(isPeriodic, Interval.get(interval), term.key),
              term
            )
          })
        })
      })
      // only replace if we were able to process without any errors
      this.#termsMapping = newTermsMapping
    }
    if (data && isArrayWithItems(data.firmsList)) {
      this.#firmsMapping = new Map<string, Firm>(
        data.firmsList?.map((firm) => [firm.firmId, firm])
      )
      this.#firmsRefMapping = new Map<string, Firm>(
        data.firmsList?.map((firm) => [firm.refId, firm])
      )
    }
    if (data && isArrayWithItems(data.fundsList)) {
      this.#fundsMapping = new Map<string, Fund>(
        data.fundsList?.map((fund) => [fund.fundId, fund])
      )
      this.#fundsRefMapping = new Map<string, Fund>(
        data.fundsList?.map((fund) => [fund.refId, fund])
      )
    }
    if (data && isArrayWithItems(data.categoriesList)) {
      this.#categoriesMapping = new Map<string, Category>(
        data.categoriesList?.map((category) => [category.categoryId, category])
      )
    }

    log.info(
      "Created new Lookup with ",
      this.#categoriesMapping.size,
      "categoryMappings",
      this.#firmsMapping.size,
      "firmsMappings",
      this.#fundsMapping.size,
      "fundsMappings",
      this.#termsMapping.size,
      "termsMappings"
    )
    log.debug(
      "Created new Lookup with",
      this.#categoriesMapping,
      this.#firmsMapping,
      this.#fundsMapping,
      this.#termsMapping
    )
  }

  hasCategories(): boolean {
    return this.#categoriesMapping.size > 0
  }

  getCategory(categoryId: string): Category | undefined {
    return this.#categoriesMapping.get(categoryId)
  }

  hasFirms(): boolean {
    return this.#firmsMapping.size > 0
  }

  getFirm(firmId: string): Firm | undefined {
    return this.#firmsMapping.get(firmId)
  }

  getFirmByRef(refId: string): Firm | undefined {
    return this.#firmsRefMapping.get(refId)
  }

  getEntity(id: string): [string, Firm] | [string, Fund] | undefined {
    const searchArray = [...this.#firmsMapping, ...this.#fundsMapping]
    return searchArray.find((entity) => entity[0] === id)
  }

  hasFunds(): boolean {
    return this.#fundsMapping.size > 0
  }

  getFund(fundId: string): Fund | undefined {
    return this.#fundsMapping.get(fundId)
  }

  getFundByRef(refId: string): Fund | undefined {
    return this.#fundsRefMapping.get(refId)
  }

  hasTerms(): boolean {
    return this.#termsMapping.size > 0
  }

  getTerm(
    isPeriodic: boolean,
    interval: Interval | string,
    key: string
  ): Term | undefined {
    return this.#termsMapping.get(termId(isPeriodic, interval, key))
  }
}
